/**
 * Налаштування колонок таблиць (рішення власника 2026-08-26): порядок і
 * показ/приховання. ОДИН механізм для всіх списків — застосовується
 * всередині InfiniteDocGrid (ag-Grid) і DataTable, сторінки не правляться.
 *
 * Персист: localStorage `grid-cols:<key>` = { order: string[], hidden: string[] }
 * (окремо від ширин — вони мають свої ключі). «скинути» повертає дефолт
 * (канон Закону дизайну #3а: бейдж → проведено → вкладення → підпис → решта).
 */
import { useEffect, useRef, useState } from 'react';
import uiPrefs from '@/store/uiPrefs';

export interface ColPrefs {
  order: string[];
  hidden: string[];
}

const PREFIX = 'grid-cols:';

// 2026-08-26: сховище — ПРОФІЛЬ юзера (store/uiPrefs: сервер + localStorage-
// дзеркало). Ключі ті самі grid-cols:<storageKey> — старі локальні
// налаштування підхоплюються і мігрують на сервер автоматично (init).
export function loadColPrefs(key: string): ColPrefs | null {
  const p = uiPrefs.get<ColPrefs>(PREFIX + key);
  if (!p || !Array.isArray(p.order) || !Array.isArray(p.hidden)) return null;
  return p;
}

export function saveColPrefs(key: string, p: ColPrefs): void {
  uiPrefs.set(PREFIX + key, p);
}

export function clearColPrefs(key: string): void {
  uiPrefs.remove(PREFIX + key);
}

/** Підписка на зміну prefs (прихід серверних після init). */
export function subscribeColPrefs(key: string, cb: () => void): () => void {
  return uiPrefs.subscribe(PREFIX + key, cb);
}

/** Колонка вибору рядків (чекбокс). Це елемент керування, а не дані. */
export const SELECT_COL_ID = '_sel';

/** Статус-колонки (Закон дизайну #3а): бейдж міграції → проведено → вкладення → підпис. */
export const STATUS_COL_IDS = ['_badge', 'posted', '_att', 'sign_control'];

/** Порядок + приховання до масиву колонок. Невідомі id (нові колонки
 *  після оновлення) лишаються на своїх місцях у хвості.
 *
 *  ч.1033: колонка вибору (`_sel`) — ЗАВЖДИ перша і не ховається. Інакше
 *  у користувача зі збереженим порядком нова колонка галочок ставала б у
 *  хвіст (як будь-яка невідома) — у самому кінці таблиці.
 *
 *  ч.1184: нова СТАТУС-колонка (проведено / вкладення / підпис), якої немає у
 *  збереженому порядку, стає на КАНОНІЧНЕ місце — одразу після свого попередника
 *  у порядку за замовчуванням, а не у хвіст (бій: у власника збережений порядок
 *  списку надходжень — додана колонка «проведено» опинилась би останньою). */
export function applyColPrefs<C>(
  cols: C[], prefs: ColPrefs | null, idOf: (c: C) => string,
): C[] {
  const sel = cols.filter((c) => idOf(c) === SELECT_COL_ID);
  const data = cols.filter((c) => idOf(c) !== SELECT_COL_ID);
  if (!prefs) return [...sel, ...data];
  const hidden = new Set(prefs.hidden);
  const pos = new Map(prefs.order.map((id, i) => [id, i]));
  const known = data.filter((c) => pos.has(idOf(c)));
  const unknown = data.filter((c) => !pos.has(idOf(c)));
  known.sort((a, b) => pos.get(idOf(a))! - pos.get(idOf(b))!);
  const merged = [...known];
  const tail: C[] = [];
  for (const c of unknown) {
    if (!STATUS_COL_IDS.includes(idOf(c))) { tail.push(c); continue; }
    let at = -1;
    for (let i = data.indexOf(c) - 1; i >= 0; i -= 1) {
      const j = merged.indexOf(data[i] as C);
      if (j >= 0) { at = j; break; }
    }
    merged.splice(at + 1, 0, c);
  }
  return [...sel, ...[...merged, ...tail].filter((c) => !hidden.has(idOf(c)))];
}

export interface ColItem {
  id: string;
  label: string;
}

interface BtnProps {
  /** Усі колонки у ПОТОЧНОМУ порядку відображення (включно з прихованими). */
  items: ColItem[];
  hidden: string[];
  onChange: (order: string[], hidden: string[]) => void;
  onReset: () => void;
}

/** Кнопка «колонки» + попап зі списком (drag-порядок, чекбокси показу). */
export default function ColumnSettingsButton({ items, hidden, onChange, onReset }: BtnProps) {
  const [open, setOpen] = useState(false);
  const dragFrom = useRef<number | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => {
      if (!hostRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);

  const hiddenSet = new Set(hidden);
  const order = items.map((i) => i.id);

  const move = (from: number, to: number) => {
    if (from === to) return;
    const next = [...order];
    const [id] = next.splice(from, 1);
    next.splice(to, 0, id!);
    onChange(next, hidden);
  };

  return (
    <div ref={hostRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        title="Порядок і показ колонок цієї таблиці"
        aria-label="Налаштувати колонки"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 24, height: 22, padding: 0, color: 'var(--muted)',
          background: 'transparent', border: 'none', cursor: 'pointer',
        }}>
        {/* Шестерёнка (рішення власника 2026-08-26) — SVG, не емодзі. */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '110%', zIndex: 120,
          background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 10px 30px rgba(15,23,42,0.18)', padding: '0.4rem',
          minWidth: 230, maxHeight: 360, overflowY: 'auto',
        }}>
          {items.map((it, idx) => (
            <div key={it.id}
              draggable
              onDragStart={() => { dragFrom.current = idx; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragFrom.current !== null) move(dragFrom.current, idx);
                dragFrom.current = null;
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '3px 4px', borderRadius: 5, cursor: 'grab',
                fontSize: '0.82rem',
              }}>
              <span style={{ color: 'var(--muted)', cursor: 'grab' }} title="Перетягнути">⠿</span>
              <input type="checkbox" checked={!hiddenSet.has(it.id)}
                onChange={(e) => {
                  const nh = e.target.checked
                    ? hidden.filter((h) => h !== it.id)
                    : [...hidden, it.id];
                  onChange(order, nh);
                }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.label}
              </span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4,
            display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => { onReset(); setOpen(false); }}
              style={{ fontSize: '0.76rem', padding: '2px 8px' }}>
              скинути
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
